import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  activateAgentSkillVersion,
  attachAgentSkill,
  deleteAgentSkillVersion,
  listAgentSkillVersions,
  listAgentSkills,
  publishAgentSkillVersion,
  type PublishVersionInput,
} from '@/services/skills';
import type { AgentSkillList, SkillAttachment, SkillVersion } from './types';
import { skillKeys } from './queryKeys';

export function useAgentSkillsQuery(agentId: string | null): UseQueryResult<AgentSkillList> {
  return useQuery<AgentSkillList>({
    queryKey: skillKeys.agentList(agentId ?? ''),
    queryFn: () => listAgentSkills(agentId as string),
    enabled: (agentId ?? '').length > 0,
  });
}

export function useAttachAgentSkill(agentId: string | null) {
  const queryClient = useQueryClient();
  const mutation = useMutation<SkillAttachment, Error, string>({
    mutationFn: (skillId) => attachAgentSkill(agentId as string, skillId),
    onSuccess: () => {
      if ((agentId ?? '').length > 0) {
        void queryClient.invalidateQueries({ queryKey: skillKeys.agentList(agentId as string) });
      }
    },
  });
  return {
    attach: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}

export function useAgentSkillVersionsQuery(
  agentId: string | null,
  skillId: string | null,
): UseQueryResult<SkillVersion[]> {
  return useQuery<SkillVersion[]>({
    queryKey: skillKeys.agentVersions(agentId ?? '', skillId ?? ''),
    queryFn: () => listAgentSkillVersions(agentId as string, skillId as string),
    enabled: (agentId ?? '').length > 0 && (skillId ?? '').length > 0,
  });
}

function invalidateSkillLifecycle(
  queryClient: ReturnType<typeof useQueryClient>,
  agentId: string | null,
  skillId: string,
) {
  if ((agentId ?? '').length === 0) return;
  void queryClient.invalidateQueries({ queryKey: skillKeys.agentList(agentId as string) });
  void queryClient.invalidateQueries({ queryKey: skillKeys.agentVersions(agentId as string, skillId) });
  void queryClient.invalidateQueries({ queryKey: skillKeys.detail(skillId) });
}

export function usePublishAgentSkillVersion(agentId: string | null, skillId: string | null) {
  const queryClient = useQueryClient();
  const mutation = useMutation<SkillVersion, Error, PublishVersionInput & { activate?: boolean }>({
    mutationFn: (input) => publishAgentSkillVersion(agentId as string, skillId as string, input),
    onSuccess: () => {
      if (skillId) invalidateSkillLifecycle(queryClient, agentId, skillId);
    },
  });
  return {
    publish: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}

export function useActivateAgentSkillVersion(agentId: string | null, skillId: string | null) {
  const queryClient = useQueryClient();
  const mutation = useMutation<SkillVersion, Error, string>({
    mutationFn: (versionId) => activateAgentSkillVersion(agentId as string, skillId as string, versionId),
    onSuccess: () => {
      if (skillId) invalidateSkillLifecycle(queryClient, agentId, skillId);
    },
  });
  return {
    activate: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}

export function useDeleteAgentSkillVersion(agentId: string | null, skillId: string | null) {
  const queryClient = useQueryClient();
  const mutation = useMutation<void, Error, string>({
    mutationFn: (versionId) => deleteAgentSkillVersion(agentId as string, skillId as string, versionId),
    onSuccess: () => {
      if (skillId) invalidateSkillLifecycle(queryClient, agentId, skillId);
    },
  });
  return {
    deleteVersion: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
